// The "what should we call the monkey?" poll (owner, 2026-11-04): after a call, a learner votes for one name.
// The names are product names, the same in every language, so they are never passed through `t(...)`; only
// the words around them are translated. Keep this file free of app imports: the fixture and tests read it.

export const TUTOR_NAME_POLL_ID = "tutor_name_2026_11";

// Stable ids: analytics, the Worker and the stored votes all use these, never the label.
export const TUTOR_NAME_PRESET_OPTIONS = [
  { optionId: "nana", label: "nana" },
  { optionId: "monkey", label: "monkey" },
  { optionId: "professor_m", label: "professor m" },
  { optionId: "coco", label: "coco" },
  { optionId: "mika", label: "mika" },
  { optionId: "lil_jungle", label: "lil jungle" },
] as const;

export type TutorNamePresetOptionId = (typeof TUTOR_NAME_PRESET_OPTIONS)[number]["optionId"];
export type TutorNamePresetOption = (typeof TUTOR_NAME_PRESET_OPTIONS)[number];

export function isTutorNamePresetOptionId(value: string): value is TutorNamePresetOptionId {
  return TUTOR_NAME_PRESET_OPTIONS.some((presetOption) => presetOption.optionId === value);
}

// A name another learner wrote in, as the Worker lists it (already cleaned and moderated there).
export interface TutorNameCommunitySuggestion {
  suggestionId: string;
  label: string;
}

export type TutorNamePollChoice =
  | { kind: "preset"; optionId: TutorNamePresetOptionId }
  | { kind: "suggestion"; suggestionId: string }
  | { kind: "other"; text: string };

export interface TutorNamePollResultRow {
  // A preset's optionId, or a suggestion's id. "other" collects the write-ins the Worker does not list by name.
  resultId: string;
  label: string;
  voteCount: number;
  kind: "preset" | "suggestion" | "other";
}

export interface TutorNamePollResults {
  totalVoteCount: number;
  rows: TutorNamePollResultRow[];
}

export const TUTOR_NAME_WRITE_IN_MAX_LENGTH = 24;

// 32-bit FNV-1a. Seeds the per-learner order so a learner who opens the poll twice sees the same order.
function fnv1aHash(text: string): number {
  let hash = 0x811c9dc5;
  for (let index = 0; index < text.length; index += 1) {
    hash ^= text.charCodeAt(index);
    hash = Math.imul(hash, 0x01000193) >>> 0;
  }
  return hash >>> 0;
}

// mulberry32: a small seeded generator, enough for a fair shuffle of six names.
function createSeededRandom(seed: number): () => number {
  let state = seed >>> 0;
  return () => {
    state = (state + 0x6d2b79f5) >>> 0;
    let mixed = state;
    mixed = Math.imul(mixed ^ (mixed >>> 15), mixed | 1);
    mixed ^= mixed + Math.imul(mixed ^ (mixed >>> 7), mixed | 61);
    return ((mixed ^ (mixed >>> 14)) >>> 0) / 4294967296;
  };
}

/**
 * The preset names in this learner's own random order (Fisher-Yates, seeded by the account id), so each
 * learner gets a different order and position bias averages out across voters. "other" is not part of it:
 * it always comes last, because it opens a text box.
 */
export function getShuffledTutorNamePresetOptions(assignmentUnitId: string): TutorNamePresetOption[] {
  const shuffledOptions: TutorNamePresetOption[] = [...TUTOR_NAME_PRESET_OPTIONS];
  const random = createSeededRandom(fnv1aHash(`${TUTOR_NAME_POLL_ID}:${assignmentUnitId}`));
  for (let index = shuffledOptions.length - 1; index > 0; index -= 1) {
    const swapIndex = Math.floor(random() * (index + 1));
    [shuffledOptions[index], shuffledOptions[swapIndex]] = [shuffledOptions[swapIndex], shuffledOptions[index]];
  }
  return shuffledOptions;
}

/**
 * Trims, collapses spaces and lowercases a written-in name (the presets are lowercase too). `toLowerCase`, not
 * `toLocaleLowerCase`: the same typed name must become the same string on every device, whatever its
 * language (a Turkish browser would turn "I" into "ı").
 */
export function normalizeTutorNameWriteIn(text: string): string {
  return text.normalize("NFKC").replace(/\s+/g, " ").trim().toLowerCase().slice(0, TUTOR_NAME_WRITE_IN_MAX_LENGTH);
}

/**
 * Whether a written-in name may be sent. Letters (any script), spaces, apostrophes, dots and hyphens only:
 * no digits, links or emails, which is where most junk and personal data would come from. The Worker
 * applies the same rule and its own word filter before it lists a name to other learners.
 */
export function isTutorNameWriteInValid(text: string): boolean {
  const normalizedText = normalizeTutorNameWriteIn(text);
  if (normalizedText.length < 2) return false;
  return /^[\p{L}\p{M}][\p{L}\p{M} '’.-]*$/u.test(normalizedText);
}

/**
 * Whole-number percentages that add up to exactly 100 (largest remainder), so a bar chart never reads
 * 99% or 101% in total. Zero votes gives every row 0.
 */
export function getTutorNamePollPercentages(voteCounts: number[]): number[] {
  const totalVoteCount = voteCounts.reduce((sum, voteCount) => sum + voteCount, 0);
  if (totalVoteCount <= 0) return voteCounts.map(() => 0);
  const exactPercentages = voteCounts.map((voteCount) => (voteCount / totalVoteCount) * 100);
  const roundedDownPercentages = exactPercentages.map((exactPercentage) => Math.floor(exactPercentage));
  let percentagePointsLeft = 100 - roundedDownPercentages.reduce((sum, percentage) => sum + percentage, 0);
  const indexesByRemainder = exactPercentages
    .map((exactPercentage, index) => ({ index, remainder: exactPercentage - Math.floor(exactPercentage) }))
    .sort((first, second) => second.remainder - first.remainder);
  for (const { index } of indexesByRemainder) {
    if (percentagePointsLeft <= 0) break;
    roundedDownPercentages[index] += 1;
    percentagePointsLeft -= 1;
  }
  return roundedDownPercentages;
}
